import { services } from "../config/services";
import { customFetch } from "../customFetch";
import {
  AssignGroupConfigResponse,
  EndpointsResponse,
  EndpointStatus,
  EndpointConfigType,
  ServiceName,
} from "../types/response/endpoints";
import { GroupConfigRequest } from "./groupConfigs";
import { Sorter } from "../sorter";

export interface EndpointSelectionCriteria {
  name?: string;
  excludeName?: string;
  companyId?: string;
  groupConfigId?: string;
}

export interface EndpointsFilters {
  state: "All" | boolean;
  status: "All" | EndpointStatus;
  location: string;
  operatingSystem: string;
  aggerVersion: string;
  groupConfig: string;
  hostName: string;
}

export interface EndpointsSyncFilters {
  location: string;
  operatingSystem: string;
  aggerVersion: string;
  groupConfig: string;
  hostName: string;
}

export type EndpointConfigRequest = Omit<
  GroupConfigRequest,
  "name" | "platform"
> & {
  groupConfigId?: string | null;
};

export const endpoints = {
  getEndpoints: (
    page: number,
    pageSize: number,
    companyId: string,
    filters: EndpointsFilters,
    sorter?: Sorter,
  ) => {
    let url = `${services.endpoints}?CompanyId=${companyId}&PageNumber=${page}&PageSize=${pageSize}`;
    if (filters.state !== "All") {
      url += `&Online=${filters.state}`;
    }
    if (filters.status !== "All") {
      url += `&Status=${filters.status}`;
    }
    if (filters.location !== "All") {
      url += `&PublicIpCountryCode=${filters.location}`;
    }
    if (filters.operatingSystem !== "All") {
      url += `&OperatingSystem=${filters.operatingSystem}`;
    }
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.groupConfig !== "All") {
      if (filters.groupConfig === "Custom") {
        url += `&ConfigType=${EndpointConfigType.Endpoint}`;
      } else {
        url += `&GroupConfigId=${filters.groupConfig}`;
      }
    }
    if (filters.hostName !== "") {
      url += `&Name=${filters.hostName}`;
    }
    if (sorter) {
      url += `&OrderBy=${sorter.orderBy}&OrderDirection=${sorter.orderDirection}`;
    }
    return customFetch(url);
  },
  getEndpointsByActiveHashSha256: (
    page: number,
    sha256: string,
    companyId: string,
  ) =>
    customFetch(
      `${services.endpoints}?ActiveHashSha256=${sha256}&CompanyId=${companyId}&PageNumber=${page}&PageSize=20`,
    ),
  getEndpointsByLicenseId: (
    page: number,
    licenseId: string,
    companyId: string,
  ) =>
    customFetch(
      `${services.endpoints}?LicenseId=${licenseId}&CompanyId=${companyId}&PageNumber=${page}&PageSize=20`,
    ),
  getEndpoint: (endpointId: string) =>
    customFetch(`${services.endpoints}/${endpointId}`),
  getEndpointConfig: (endpointId: string) =>
    customFetch(`${services.endpoints}/${endpointId}/config`),
  changeEndpointConfig: (endpointId: string, config: EndpointConfigRequest) =>
    customFetch(`${services.endpoints}/${endpointId}/config`, {
      method: "PUT",
      body: JSON.stringify(config),
    }),
  approveEndpoint: (id: string) =>
    customFetch(`${services.endpoints}/${id}/approve`, { method: "POST" }),
  denyEndpoint: (id: string) =>
    customFetch(`${services.endpoints}/${id}/deny`, { method: "POST" }),
  approveEndpoints: (ids: string[]) =>
    customFetch(`${services.endpoints}/approve`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  denyEndpoints: (ids: string[]) =>
    customFetch(`${services.endpoints}/deny`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  changeLicense: (endpointId: string, licenseId: string) =>
    customFetch(`${services.endpoints}/${endpointId}`, {
      method: "PUT",
      body: JSON.stringify({ licenseId }),
    }),
  assignLicense: (endpointIds: string[], licenseId: string) =>
    customFetch(`${services.endpoints}/assign-license`, {
      method: "POST",
      body: JSON.stringify({ endpointIds, licenseId }),
    }),
  assignGroupConfig: (
    endpointIds: string[],
    groupConfigId?: string,
    criteria?: EndpointSelectionCriteria,
  ): Promise<AssignGroupConfigResponse> =>
    customFetch(`${services.endpoints}/assign-group-config`, {
      method: "POST",
      body: JSON.stringify({ endpointIds, groupConfigId, criteria }),
    }),
  searchEndpointsByName: (
    name: string,
    page: number,
    companyId?: string,
    groupConfigId?: string,
    excludeName?: string,
  ): Promise<EndpointsResponse> => {
    let url = `${services.endpoints}?PageNumber=${page}&PageSize=10`;
    if (name) {
      url += `&Name=${encodeURIComponent(name)}`;
    }
    if (companyId) {
      url += `&CompanyId=${companyId}`;
    }
    if (groupConfigId) {
      url += `&GroupConfigId=${groupConfigId}`;
    }
    if (excludeName) {
      url += `&ExcludeName=${encodeURIComponent(excludeName)}`;
    }
    return customFetch(url);
  },
  getGroupConfigEndpoints: (groupConfigId: string, page: number) =>
    customFetch(
      `${services.endpoints}?GroupConfigId=${groupConfigId}&PageNumber=${page}`,
    ),
  getEndpointsLocations: (companyId: string, filters: EndpointsSyncFilters) => {
    let url = `${services.endpoints}/locations?CompanyId=${companyId}`;
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.operatingSystem !== "All") {
      url += `&OperatingSystem=${filters.operatingSystem}`;
    }
    if (filters.groupConfig !== "All") {
      if (filters.groupConfig === "Custom") {
        url += `&ConfigType=${EndpointConfigType.Endpoint}`;
      } else {
        url += `&GroupConfigId=${filters.groupConfig}`;
      }
    }
    if (filters.hostName !== "") {
      url += `&Name=${filters.hostName}`;
    }
    return customFetch(url);
  },
  getEndpointsOperatingSystems: (
    companyId: string,
    filters: EndpointsSyncFilters,
  ) => {
    let url = `${services.endpoints}/operating-systems?CompanyId=${companyId}`;
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.location !== "All") {
      url += `&PublicIpCountryCode=${filters.location}`;
    }
    if (filters.groupConfig !== "All") {
      if (filters.groupConfig === "Custom") {
        url += `&ConfigType=${EndpointConfigType.Endpoint}`;
      } else {
        url += `&GroupConfigId=${filters.groupConfig}`;
      }
    }
    if (filters.hostName !== "") {
      url += `&Name=${filters.hostName}`;
    }
    return customFetch(url);
  },
  getEndpointsAggerVersions: (
    companyId: string,
    filters: EndpointsSyncFilters,
  ) => {
    let url = `${services.endpoints}/versions?CompanyId=${companyId}`;
    if (filters.operatingSystem !== "All") {
      url += `&OperatingSystem=${filters.operatingSystem}`;
    }
    if (filters.location !== "All") {
      url += `&PublicIpCountryCode=${filters.location}`;
    }
    if (filters.groupConfig !== "All") {
      if (filters.groupConfig === "Custom") {
        url += `&ConfigType=${EndpointConfigType.Endpoint}`;
      } else {
        url += `&GroupConfigId=${filters.groupConfig}`;
      }
    }
    if (filters.hostName !== "") {
      url += `&Name=${filters.hostName}`;
    }
    return customFetch(url);
  },
  getEndpointsGroupConfigs: (
    companyId: string,
    filters: EndpointsSyncFilters,
  ) => {
    let url = `${services.endpoints}/group-configs?CompanyId=${companyId}`;
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.operatingSystem !== "All") {
      url += `&OperatingSystem=${filters.operatingSystem}`;
    }
    if (filters.location !== "All") {
      url += `&PublicIpCountryCode=${filters.location}`;
    }
    if (filters.hostName !== "") {
      url += `&Name=${filters.hostName}`;
    }
    return customFetch(url);
  },
  getEndpointApplications: (
    endpointId: string,
    page: number,
    search?: string,
  ) => {
    let url = `${services.endpoints}/applications?EndpointId=${endpointId}&PageNumber=${page}&PageSize=20`;
    if (search) {
      url += `&Name=${search}`;
    }
    return customFetch(url);
  },
  getEndpointApplication: (applicationId: string) =>
    customFetch(`${services.endpoints}/applications/${applicationId}`),
  getMaintenanceKey: (endpointId: string) =>
    customFetch(`${services.endpoints}/${endpointId}/maintenance-key`, {
      method: "POST",
    }),
  getEndpointServices: (endpointId: string) =>
    customFetch(`${services.endpoints}/${endpointId}/services`),
  installEndpointService: (endpointId: string, serviceName: ServiceName) =>
    customFetch(
      `${services.endpoints}/${endpointId}/services/${serviceName}/install`,
      {
        method: "POST",
      },
    ),
  uninstallEndpointService: (endpointId: string, serviceName: ServiceName) =>
    customFetch(
      `${services.endpoints}/${endpointId}/services/${serviceName}/uninstall`,
      {
        method: "POST",
      },
    ),
  installEndpointServiceBulk: (
    endpointIds: string[],
    serviceName: ServiceName,
  ) =>
    customFetch(`${services.endpoints}/services/${serviceName}/install`, {
      method: "POST",
      body: JSON.stringify({ endpointIds }),
    }),
  uninstallEndpointServiceBulk: (
    endpointIds: string[],
    serviceName: ServiceName,
  ) =>
    customFetch(`${services.endpoints}/services/${serviceName}/uninstall`, {
      method: "POST",
      body: JSON.stringify({ endpointIds }),
    }),
  cancelEndpointServiceOperation: (
    endpointId: string,
    serviceName: ServiceName,
  ) =>
    customFetch(
      `${services.endpoints}/${endpointId}/services/${serviceName}/cancel`,
      {
        method: "POST",
      },
    ),
  deleteEndpoints: (endpointIds: string[]) =>
    customFetch(services.endpoints, {
      method: "DELETE",
      body: JSON.stringify({
        ids: endpointIds,
      }),
    }),
};
